import React, { useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Zap, Timer, Waves, Thermometer, ShoppingCart, Check, Star, Sparkles, Power, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Product images - Iron close-ups and detail shots (no branding on clothes)
const productImages = [
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/awleu9gc_DSC04270.jpg',
    alt: 'Winamor Perfect Pass keratin iron LCD display showing pass count number 8 - Australia best keratin straightener'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/rgmn8gei_DSC04273.jpg',
    alt: 'Winamor Perfect Pass LCD screen displaying 230°C temperature setting for professional keratin treatment'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/h3d7rvu8_DSC03497.jpg',
    alt: 'Winamor Perfect Pass keratin iron on marble surface showing premium rose gold design and Winamor logo'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/yag1i786_DSC03507.jpg',
    alt: 'Professional hairdresser holding Winamor Perfect Pass keratin iron ready for salon keratin treatment'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/qx71rwzz_DSC03496.jpg',
    alt: 'Complete Winamor Perfect Pass keratin iron with 3 meter swivel cord - professional hair straightener Australia'
  },
];

// In-use/styling images (no visible branding on clothes)
const stylingImages = [
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/v4ikmef7_MDM09428.jpg',
    alt: 'Creating bouncy curls with Winamor Perfect Pass iron rounded edges - professional hair styling'
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/7xgvjhte_MDM09427.jpg',
    alt: 'Keratin treatment straightening with Winamor Perfect Pass - smooth silky hair results'
  },
];

const features = [
  {
    id: 'counter',
    title: 'Keratin Master',
    subtitle: 'Built-in Pass Counter',
    description: 'Never lose count again. Our precision pass-counting technology ensures every section gets the perfect number of passes for flawless Keratin results.',
    icon: Timer,
    highlight: 'Set 3-30 passes',
    large: true
  },
  {
    id: 'curler',
    title: 'Effortless Curler',
    subtitle: 'Rounded Edges',
    description: 'Snag-free titanium plates with rounded edges make bouncy curls as easy as a straight pass. One tool, endless styles.',
    icon: Waves,
    highlight: 'Two modes'
  },
  {
    id: 'normal',
    title: 'Normal Iron Mode',
    subtitle: 'Turn Off Vibration',
    description: 'Switch off the counting mode and vibration alerts with one button press. Use it as a regular professional iron anytime.',
    icon: Power,
    highlight: 'One-click toggle'
  },
  {
    id: 'vibration',
    title: 'Vibration Alerts',
    subtitle: 'Hands-Free Counting',
    description: 'No more counting in your head. The iron vibrates gently to tell you when each section is perfect. Can be turned off anytime.',
    icon: Zap,
    highlight: 'Smart alerts'
  }
];

export const ProductShowcase = ({ id }) => {
  const [loading, setLoading] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showTradeIn, setShowTradeIn] = useState(true); // Default to trade-in price

  const handleCheckout = async (productType) => {
    setLoading(productType);
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        product_type: productType,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Unable to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <section id={id} className="section bg-[#0F0720] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-purple-600/5 rounded-full blur-3xl" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="text-purple-400 text-sm font-semibold uppercase tracking-wider mb-4 block">
            The Perfect Pass Technology
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Why Choose <span className="text-gradient italic">Winamor?</span>
          </h2>
          <p className="text-purple-200 text-lg max-w-2xl mx-auto">
            Designed by hairdressers, for hairdressers. The world's first hair straightener 
            with precision pass-counting technology.
          </p>
        </div>

        {/* Bento Grid Features */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <div 
              key={feature.id}
              className={`feature-card card-hover animate-fade-in-up ${feature.large ? 'lg:col-span-2' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">{feature.title}</h3>
                  <p className="text-purple-400 text-sm">{feature.subtitle}</p>
                </div>
              </div>
              <p className="text-purple-200 mb-4">{feature.description}</p>
              <span className="value-badge">
                <Check className="w-4 h-4" />
                {feature.highlight}
              </span>
            </div>
          ))}
        </div>

        {/* Product Cards */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Product */}
          <div className="lg:col-span-2 glass-card p-8 relative overflow-hidden animate-fade-in-up">
            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Image Gallery */}
              <div>
                <div className="product-image-container aspect-square mb-4 rounded-xl overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200">
                  <img 
                    src={productImages[activeImage].src}
                    alt={productImages[activeImage].alt}
                    className="w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {productImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(idx)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors bg-gray-100 ${
                        activeImage === idx ? 'border-purple-500' : 'border-transparent hover:border-purple-500/50'
                      }`}
                      aria-label={`View product image ${idx + 1}`}
                    >
                      <img 
                        src={img.src} 
                        alt={img.alt} 
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-purple-300 text-sm">(127 reviews)</span>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">Perfect Pass Iron</h3>
                <p className="text-purple-300 mb-4">Australia's First Precision-Count Hair Straightener for Keratin & Styling</p>
                
                {/* Price Toggle */}
                <div className="mb-4">
                  <div className="flex gap-3 mb-3">
                    <button
                      onClick={() => setShowTradeIn(false)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                        !showTradeIn 
                          ? 'bg-purple-500 text-white' 
                          : 'bg-purple-500/20 text-purple-300 hover:bg-purple-500/30'
                      }`}
                    >
                      Full Price
                    </button>
                    <button
                      onClick={() => setShowTradeIn(true)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${
                        showTradeIn 
                          ? 'bg-green-500 text-white' 
                          : 'bg-green-500/20 text-green-300 hover:bg-green-500/30'
                      }`}
                    >
                      <RefreshCw className="w-4 h-4" />
                      Trade-In
                    </button>
                  </div>
                  
                  {showTradeIn ? (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
                      <div className="flex items-baseline gap-3 mb-2">
                        <span className="price-tag text-green-400">$210</span>
                        <span className="price-original">$310</span>
                      </div>
                      <p className="text-green-300 text-sm">
                        <strong>Save $100!</strong> Trade in your old iron (any brand, any condition)
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="price-tag">$310</span>
                    </div>
                  )}
                </div>
                
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-purple-200">
                    <Check className="w-4 h-4 text-green-400" /> LCD Pass Counter Display
                  </li>
                  <li className="flex items-center gap-2 text-purple-200">
                    <Check className="w-4 h-4 text-green-400" /> Vibration Alert (Can Turn Off)
                  </li>
                  <li className="flex items-center gap-2 text-purple-200">
                    <Check className="w-4 h-4 text-green-400" /> Normal Iron Mode Included
                  </li>
                  <li className="flex items-center gap-2 text-purple-200">
                    <Check className="w-4 h-4 text-green-400" /> 70-230°C Temperature Range
                  </li>
                  <li className="flex items-center gap-2 text-purple-200">
                    <Check className="w-4 h-4 text-green-400" /> 1-Year Warranty (Extendable)
                  </li>
                </ul>
                
                <Button 
                  onClick={() => handleCheckout(showTradeIn ? 'perfect_pass_iron_tradein' : 'perfect_pass_iron')}
                  disabled={loading === 'perfect_pass_iron' || loading === 'perfect_pass_iron_tradein'}
                  data-testid="buy-iron-btn"
                  className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2"
                >
                  {(loading === 'perfect_pass_iron' || loading === 'perfect_pass_iron_tradein') ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      {showTradeIn ? 'Order with Trade-In' : 'Order Now'}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Side Products */}
          <div className="space-y-6">
            {/* Master Kit */}
            <div className="glass-card p-6 animate-fade-in-up stagger-2">
              <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-400 text-xs font-bold px-3 py-1 rounded-full mb-4">
                <Sparkles className="w-3 h-3" /> BEST VALUE
              </div>
              <h4 className="text-lg font-bold text-white mb-2">Master Kit Bundle</h4>
              <p className="text-purple-300 text-sm mb-4">Iron + Keratin Sample + Professional Comb</p>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-2xl font-bold text-yellow-400 mono">$329</span>
                <span className="text-purple-400 text-sm line-through">$399</span>
              </div>
              <Button 
                onClick={() => handleCheckout('master_kit')}
                disabled={loading === 'master_kit'}
                data-testid="buy-kit-btn"
                className="btn-secondary w-full"
              >
                {loading === 'master_kit' ? 'Loading...' : 'Get the Bundle'}
              </Button>
            </div>

            {/* Thermal Spray Bump */}
            <div className="glass-card p-6 border-2 border-dashed border-purple-500/50 animate-fade-in-up stagger-3">
              <div className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">Add-On Special</div>
              <h4 className="text-lg font-bold text-white mb-2">Thermal Protectant Spray</h4>
              <p className="text-purple-300 text-sm mb-4">Essential for younger, delicate hair!</p>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-xl font-bold text-green-400 mono">$22</span>
                <span className="text-purple-400 text-sm line-through">$37</span>
                <span className="text-xs text-green-400 font-semibold">SAVE $15</span>
              </div>
              <Button 
                onClick={() => handleCheckout('thermal_spray')}
                disabled={loading === 'thermal_spray'}
                data-testid="buy-spray-btn"
                variant="outline"
                className="w-full border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
              >
                {loading === 'thermal_spray' ? 'Loading...' : 'Add to Cart'}
              </Button>
            </div>
          </div>
        </div>

        {/* Product Detail Showcase - LCD Features */}
        <div className="mt-20 animate-fade-in-up">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Precision <span className="text-gradient italic">Technology</span>
            </h3>
            <p className="text-purple-300 max-w-2xl mx-auto">
              See the LCD display in action - track your passes and temperature with crystal clarity.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Pass Counter Feature */}
            <div className="glass-card overflow-hidden group">
              <div className="aspect-video overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/awleu9gc_DSC04270.jpg"
                  alt="Winamor Perfect Pass LCD display showing pass count 8 - keratin treatment tracking technology for professional results"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h4 className="text-lg font-bold text-white mb-2">Pass Counter Mode</h4>
                <p className="text-purple-300 text-sm">
                  Set your desired pass count (3-30) and let the iron track for you. 
                  Perfect for consistent Keratin results every time.
                </p>
              </div>
            </div>
            
            {/* Temperature Feature */}
            <div className="glass-card overflow-hidden group">
              <div className="aspect-video overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/rgmn8gei_DSC04273.jpg"
                  alt="Winamor Perfect Pass temperature control LCD showing 230°C - adjustable heat settings for all hair types"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h4 className="text-lg font-bold text-white mb-2">Temperature Control</h4>
                <p className="text-purple-300 text-sm">
                  Precise heat from 70°C to 230°C in 10° increments. 
                  Match the exact needs of any hair type or treatment.
                </p>
              </div>
            </div>
            
            {/* In Action */}
            <div className="glass-card overflow-hidden group">
              <div className="aspect-video overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smart-iron-funnel/artifacts/v4ikmef7_MDM09428.jpg"
                  alt="Professional hairdresser creating bouncy curls with Winamor Perfect Pass iron rounded plates - versatile hair styling"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <h4 className="text-lg font-bold text-white mb-2">Versatile Styling</h4>
                <p className="text-purple-300 text-sm">
                  Not just for Keratin - create bouncy curls, sleek straight styles, 
                  and everything in between with rounded, snag-free plates.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
